import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useWins } from "../components/WinsGallery";

const FIELDS = [
  { name: "odds", label: "Quota totale", placeholder: "242.29" },
  { name: "stake", label: "Importo giocato €", placeholder: "5" },
  { name: "payout", label: "Vincita €", placeholder: "1211.46" },
];
const INPUT = "w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-sm focus:border-[#39FF14] outline-none";

function UploadForm({ onDone }) {
  const [form, setForm] = useState({ caption: "", odds: "", stake: "", payout: "", date: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) return toast.error("Scegli almeno una foto");
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    Object.entries(form).forEach(([k, v]) => v !== "" && fd.append(k, k === "caption" || k === "date" ? v : v.replace(",", ".")));
    setBusy(true);
    try {
      await api.post("/admin/wins", fd);
      toast.success("Vincita pubblicata");
      setForm({ caption: "", odds: "", stake: "", payout: "", date: "" });
      setFiles([]);
      e.target.reset();
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="surface-raised p-6 space-y-4" data-testid="admin-win-form">
      <div className="label">Nuova vincita</div>
      <input type="file" accept="image/png,image/jpeg,image/webp" multiple data-testid="admin-win-files"
        onChange={(e) => setFiles([...e.target.files].slice(0, 4))}
        className="block w-full text-sm text-[#A1A1AA] file:mr-4 file:border-0 file:bg-[#39FF14] file:text-black file:font-black file:uppercase file:text-xs file:px-4 file:py-2 file:cursor-pointer hover:file:bg-white" />
      <textarea value={form.caption} onChange={set("caption")} placeholder="Didascalia (es. Quota 120 con la mista corner!)" rows={2} className={INPUT} data-testid="admin-win-caption" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {FIELDS.map((f) => (
          <label key={f.name} className="text-[10px] label space-y-1">
            <span>{f.label}</span>
            <input value={form[f.name]} onChange={set(f.name)} placeholder={f.placeholder} inputMode="decimal" className={INPUT} data-testid={`admin-win-${f.name}`} />
          </label>
        ))}
        <label className="text-[10px] label space-y-1">
          <span>Data</span>
          <input type="date" value={form.date} onChange={set("date")} className={INPUT} style={{ colorScheme: "dark" }} data-testid="admin-win-date" />
        </label>
      </div>
      <button disabled={busy} data-testid="admin-win-submit" className="inline-flex items-center gap-2 bg-[#39FF14] text-black font-black uppercase text-xs px-5 py-3 disabled:opacity-50">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Pubblica
      </button>
    </form>
  );
}

export default function AdminWins() {
  const { user } = useAuth();
  const { data, mutate } = useWins();
  const [confirmId, setConfirmId] = useState(null);
  if (user === null) return <Loader2 className="animate-spin m-10" />;
  if (user?.role !== "admin") return <p className="p-10 text-sm" data-testid="admin-wins-denied">Pagina riservata all&apos;amministratore.</p>;
  const remove = async (id) => {
    if (confirmId !== id) return setConfirmId(id);
    setConfirmId(null);
    try {
      await api.delete(`/admin/wins/${id}`);
      mutate();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="min-h-screen px-6 lg:px-10 py-10 max-w-4xl mx-auto space-y-8">
      <Link to="/vincite" className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white" data-testid="admin-wins-back"><ArrowLeft size={14} /> Galleria vincite</Link>
      <h1 className="heading text-4xl font-black tracking-tighter">Gestisci vincite</h1>
      <UploadForm onDone={mutate} />
      <div className="space-y-2" data-testid="admin-win-list">
        {data?.photos.filter((p) => !p.pinned).map((p) => (
          <div key={p.id} className="surface-raised p-3 flex items-center gap-3" data-testid={`admin-win-item-${p.id}`}>
            <img src={p.images[0]} alt="" className="w-12 h-16 object-cover object-top" />
            <div className="flex-1 min-w-0 text-sm truncate">{p.caption || "Senza didascalia"} {p.odds != null && `· Q ${p.odds}`}</div>
            <button onClick={() => remove(p.id)} data-testid={`admin-win-delete-${p.id}`} className="flex items-center gap-1 text-xs font-bold text-[#FF3B30] hover:text-white">
              <Trash2 size={16} /> {confirmId === p.id && "Conferma"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
